import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import "./tableePromotions.css";

const emptyForm = {
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  maximumDiscount: "",
  minimumSubtotal: "0",
  startsAt: "",
  endsAt: "",
  maximumAccounts: "",
  isActive: true,
};

const statusLabels = {
  active: "Activo",
  scheduled: "Programado",
  expired: "Expirado",
  exhausted: "Máximo alcanzado",
  disabled: "Desactivado",
};

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function toLocalDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDate(value) {
  if (!value) return "Sin límite";
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

const promotionMonthNames = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function parseLocalDateTime(value) {
  if (!value) return null;
  const [datePart, timePart = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  const date = new Date(year, month - 1, day, hours || 0, minutes || 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function localDateTimeValue(date) {
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function PromotionSelect({ ariaLabel, value, options, onChange, disabled = false, compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const optionsId = useId();
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick, true);
    document.addEventListener("focusin", closeOnOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick, true);
      document.removeEventListener("focusin", closeOnOutsideClick, true);
    };
  }, [isOpen]);

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const currentIndex = Math.max(0, options.findIndex((option) => option.value === value));
    const offset = event.key === "ArrowDown" ? 1 : -1;
    const nextIndex = (currentIndex + offset + options.length) % options.length;
    onChange(options[nextIndex].value);
    setIsOpen(true);
  }

  return (
    <div className={`tablee-promotions__custom-select${compact ? " is-compact" : ""}`} ref={selectRef}>
      <button
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={optionsId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={handleKeyDown}
      >
        <span>{selectedOption?.label || value}</span>
        <span className="material-symbols-outlined" aria-hidden="true">{isOpen ? "expand_less" : "expand_more"}</span>
      </button>
      {isOpen ? (
        <div id={optionsId} className="tablee-promotions__custom-options" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? "is-selected" : ""}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value ? <span className="material-symbols-outlined" aria-hidden="true">check</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PromotionDateTimePicker({ label, value, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState(null);
  const selectedDate = useMemo(() => parseLocalDateTime(value), [value]);
  const [viewDate, setViewDate] = useState(() => selectedDate || new Date());
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const calendarRef = useRef(null);
  const calendarId = useId();

  function positionCalendar() {
    if (!triggerRef.current) return;
    const viewportMargin = 12;
    const gap = 7;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(310, window.innerWidth - (viewportMargin * 2));
    const calendarHeight = calendarRef.current?.offsetHeight || 410;
    const spaceBelow = window.innerHeight - triggerRect.bottom - viewportMargin;
    const openBelow = spaceBelow >= calendarHeight || spaceBelow >= triggerRect.top - viewportMargin;
    const top = openBelow
      ? Math.min(triggerRect.bottom + gap, window.innerHeight - calendarHeight - viewportMargin)
      : Math.max(viewportMargin, triggerRect.top - calendarHeight - gap);
    const left = Math.min(
      Math.max(viewportMargin, triggerRect.left),
      window.innerWidth - width - viewportMargin
    );
    setCalendarPosition({ top: Math.max(viewportMargin, top), left, width });
  }

  useEffect(() => {
    if (!isOpen) return undefined;
    setViewDate(selectedDate || new Date());
    const closeOnOutsideClick = (event) => {
      const clickedPicker = pickerRef.current?.contains(event.target);
      const clickedCalendar = calendarRef.current?.contains(event.target);
      if (!clickedPicker && !clickedCalendar) setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick, true);
    document.addEventListener("focusin", closeOnOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick, true);
      document.removeEventListener("focusin", closeOnOutsideClick, true);
    };
  }, [isOpen, selectedDate]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCalendarPosition(null);
      return undefined;
    }
    positionCalendar();
    const updatePosition = () => positionCalendar();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, viewDate, value]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const leadingDays = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calendarCells = [
    ...Array.from({ length: leadingDays }, (_, index) => ({ key: `empty-${index}` })),
    ...Array.from({ length: daysInMonth }, (_, index) => ({ key: `day-${index + 1}`, day: index + 1 })),
  ];

  function selectDay(day) {
    const nextDate = selectedDate
      ? new Date(selectedDate)
      : new Date(year, month, day, 0, 0);
    nextDate.setFullYear(year, month, day);
    onChange(localDateTimeValue(nextDate));
  }

  function changeTimePart(part, nextValue) {
    const nextDate = selectedDate || new Date(year, month, 1, 0, 0);
    const updatedDate = new Date(nextDate);
    if (part === "hours") updatedDate.setHours(Number(nextValue), updatedDate.getMinutes(), 0, 0);
    else updatedDate.setMinutes(Number(nextValue), 0, 0);
    onChange(localDateTimeValue(updatedDate));
  }

  function changeMonth(offset) {
    setViewDate(new Date(year, month + offset, 1));
  }

  const displayValue = selectedDate
    ? new Intl.DateTimeFormat("es-CL", { dateStyle: "medium", timeStyle: "short" }).format(selectedDate)
    : "Sin fecha";

  return (
    <div className={`tablee-promotions__date-field${disabled ? " is-disabled" : ""}`} ref={pickerRef}>
      <span className="tablee-promotions__date-label">{label}</span>
      <button
        ref={triggerRef}
        className="tablee-promotions__date-trigger"
        type="button"
        disabled={disabled}
        aria-controls={calendarId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="material-symbols-outlined" aria-hidden="true">calendar_month</span>
        <span>{displayValue}</span>
        <span className="material-symbols-outlined" aria-hidden="true">{isOpen ? "expand_less" : "expand_more"}</span>
      </button>
      {isOpen ? createPortal((
        <div
          ref={calendarRef}
          id={calendarId}
          className="tablee-promotions__calendar"
          role="dialog"
          aria-label={`Seleccionar ${label.toLowerCase()}`}
          style={calendarPosition || { visibility: "hidden" }}
        >
          <div className="tablee-promotions__calendar-header">
            <button type="button" aria-label="Mes anterior" onClick={() => changeMonth(-1)}>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <strong>{promotionMonthNames[month]} {year}</strong>
            <button type="button" aria-label="Mes siguiente" onClick={() => changeMonth(1)}>
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <div className="tablee-promotions__calendar-grid">
            {["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"].map((weekday) => <span key={weekday}>{weekday}</span>)}
            {calendarCells.map((cell) => cell.day ? (
              <button
                type="button"
                key={cell.key}
                className={selectedDate
                  && selectedDate.getFullYear() === year
                  && selectedDate.getMonth() === month
                  && selectedDate.getDate() === cell.day ? "is-selected" : ""}
                onClick={() => selectDay(cell.day)}
              >
                {cell.day}
              </button>
            ) : <i key={cell.key} />)}
          </div>
          <div className="tablee-promotions__calendar-time">
            <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
            <span>Hora</span>
            <div>
              <PromotionSelect
                ariaLabel="Hora"
                value={selectedDate ? String(selectedDate.getHours()).padStart(2, "0") : "00"}
                options={Array.from({ length: 24 }, (_, hour) => {
                  const hourValue = String(hour).padStart(2, "0");
                  return { value: hourValue, label: hourValue };
                })}
                onChange={(nextHour) => changeTimePart("hours", nextHour)}
                compact
              />
              <b>:</b>
              <PromotionSelect
                ariaLabel="Minutos"
                value={selectedDate ? String(selectedDate.getMinutes()).padStart(2, "0") : "00"}
                options={Array.from({ length: 60 }, (_, minute) => {
                  const minuteValue = String(minute).padStart(2, "0");
                  return { value: minuteValue, label: minuteValue };
                })}
                onChange={(nextMinute) => changeTimePart("minutes", nextMinute)}
                compact
              />
            </div>
          </div>
          <div className="tablee-promotions__calendar-actions">
            <button type="button" onClick={() => onChange("")}>Limpiar</button>
            <button type="button" onClick={() => setIsOpen(false)}>Listo</button>
          </div>
        </div>
      ), document.body) : null}
    </div>
  );
}

function formFromPromotion(promotion) {
  return {
    code: promotion.code || "",
    description: promotion.description || "",
    discountType: promotion.discountType || "percentage",
    discountValue: String(promotion.discountValue ?? ""),
    maximumDiscount: promotion.maximumDiscount == null ? "" : String(promotion.maximumDiscount),
    minimumSubtotal: String(promotion.minimumSubtotal ?? 0),
    startsAt: toLocalDateTime(promotion.startsAt),
    endsAt: toLocalDateTime(promotion.endsAt),
    maximumAccounts: promotion.maximumAccounts == null ? "" : String(promotion.maximumAccounts),
    isActive: promotion.isActive !== false,
  };
}

function TableePromotions({ apiPrefix = "/tablee", storeName = "Tablee" } = {}) {
  const [promotions, setPromotions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [usagePromotion, setUsagePromotion] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/promotions`), { headers: getSessionHeaders() });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "No se pudieron cargar los códigos.");
      setPromotions(data.promotions || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const visiblePromotions = useMemo(
    () => promotions.filter((promotion) => filter === "all" || promotion.status === filter),
    [promotions, filter]
  );

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setMessage("");
    setModalOpen(true);
  }

  function openEdit(promotion) {
    setEditing(promotion);
    setForm(formFromPromotion(promotion));
    setMessage("");
    setModalOpen(true);
  }

  async function generateCode() {
    const response = await fetch(apiUrl(`${apiPrefix}/promotions/generate-code`), {
      method: "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify({ prefix: "TABLEE" }),
    });
    const data = await response.json();
    if (data.success) setForm((current) => ({ ...current, code: data.code }));
    else setMessage(data.message || "No se pudo generar el código.");
  }

  async function savePromotion(event) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl(editing ? `${apiPrefix}/promotions/${editing.id}` : `${apiPrefix}/promotions`), {
        method: editing ? "PUT" : "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          ...form,
          startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
          endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "No se pudo guardar el código.");
      setModalOpen(false);
      await loadPromotions();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(promotion) {
    const response = await fetch(apiUrl(`${apiPrefix}/promotions/${promotion.id}/active`), {
      method: "PATCH",
      headers: getSessionHeaders(),
      body: JSON.stringify({ isActive: !promotion.isActive }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      setMessage(data.message || "No se pudo cambiar el estado.");
      return;
    }
    await loadPromotions();
  }

  async function duplicatePromotion(promotion) {
    const response = await fetch(apiUrl(`${apiPrefix}/promotions/${promotion.id}/duplicate`), {
      method: "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify({}),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      setMessage(data.message || "No se pudo duplicar el código.");
      return;
    }
    await loadPromotions();
    setMessage(`Se creó ${data.promotion.code} desactivado para que puedas revisarlo.`);
  }

  async function openUsage(promotion) {
    setUsagePromotion(promotion);
    setRedemptions([]);
    const response = await fetch(apiUrl(`${apiPrefix}/promotions/${promotion.id}/redemptions`), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) setRedemptions(data.redemptions || []);
    else setMessage(data.message || "No se pudo cargar el uso del código.");
  }

  return (
    <section className="tablee-promotions">
      <header className="tablee-promotions__header">
        <div>
          <span>{storeName}</span>
          <h1>Códigos promocionales</h1>
          <p>Configura descuentos que vencen por fecha o al alcanzar el máximo de cuentas.</p>
        </div>
        <button type="button" onClick={openCreate}>
          <span className="material-symbols-outlined">add</span>
          Nuevo código
        </button>
      </header>

      {message ? <div className="tablee-promotions__message">{message}</div> : null}

      <nav className="tablee-promotions__filters" aria-label="Filtrar promociones">
        {["all", "active", "scheduled", "exhausted", "expired", "disabled"].map((status) => (
          <button key={status} type="button" className={filter === status ? "is-active" : ""} onClick={() => setFilter(status)}>
            {status === "all" ? "Todos" : statusLabels[status]}
          </button>
        ))}
      </nav>

      <div className={`tablee-promotions__table-wrap${loading || !visiblePromotions.length ? " is-empty" : ""}`}>
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Descuento</th>
              <th>Vigencia</th>
              <th>Cuentas aplicadas</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visiblePromotions.map((promotion) => (
              <tr key={promotion.id}>
                <td><strong>{promotion.code}</strong><small>{promotion.description || "Sin descripción"}</small></td>
                <td>
                  <strong>{promotion.discountType === "percentage" ? `${promotion.discountValue}%` : formatMoney(promotion.discountValue)}</strong>
                  <small>Compra mínima {formatMoney(promotion.minimumSubtotal)}</small>
                </td>
                <td><small>Desde {formatDate(promotion.startsAt)}</small><small>Hasta {formatDate(promotion.endsAt)}</small></td>
                <td>
                  <button className="tablee-promotions__usage" type="button" onClick={() => openUsage(promotion)}>
                    {promotion.appliedAccounts + promotion.reservedAccounts} / {promotion.maximumAccounts ?? "∞"}
                  </button>
                  {promotion.reservedAccounts ? <small>{promotion.reservedAccounts} reservadas</small> : null}
                </td>
                <td><span className={`tablee-promotions__status is-${promotion.status}`}>{statusLabels[promotion.status]}</span></td>
                <td>
                  <div className="tablee-promotions__actions">
                    <button type="button" title="Editar" onClick={() => openEdit(promotion)}><span className="material-symbols-outlined">edit</span></button>
                    <button type="button" title="Duplicar" onClick={() => duplicatePromotion(promotion)}><span className="material-symbols-outlined">content_copy</span></button>
                    <button type="button" title={promotion.isActive ? "Desactivar" : "Activar"} onClick={() => toggleActive(promotion)}>
                      <span className="material-symbols-outlined">{promotion.isActive ? "pause_circle" : "play_circle"}</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {loading ? <tr><td colSpan="6" className="tablee-promotions__empty">Cargando códigos...</td></tr> : null}
            {!loading && !visiblePromotions.length ? <tr><td colSpan="6" className="tablee-promotions__empty">No hay códigos en este estado.</td></tr> : null}
          </tbody>
        </table>
      </div>

      {modalOpen ? (
        <div className="tablee-promotions__backdrop" role="presentation" onMouseDown={() => setModalOpen(false)}>
          <form className="tablee-promotions__modal" onSubmit={savePromotion} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><span>Promoción</span><h2>{editing ? `Editar ${editing.code}` : "Nuevo código"}</h2></div><button type="button" onClick={() => setModalOpen(false)}>×</button></header>
            {editing?.appliedAccounts > 0 ? <p className="tablee-promotions__notice">Este código ya fue usado. Sus reglas de descuento están bloqueadas; todavía puedes cambiar vigencia, máximo y estado.</p> : null}
            <div className="tablee-promotions__form-grid">
              <label className="is-wide">Código<div className="tablee-promotions__code-input"><input value={form.code} disabled={editing?.appliedAccounts > 0} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} required /><button type="button" disabled={editing?.appliedAccounts > 0} onClick={generateCode}>Generar</button></div></label>
              <label className="is-wide">Descripción<input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
              <label>
                Tipo
                <PromotionSelect
                  ariaLabel="Tipo de descuento"
                  value={form.discountType}
                  disabled={editing?.appliedAccounts > 0}
                  options={[
                    { value: "percentage", label: "Porcentaje" },
                    { value: "fixed", label: "Monto fijo CLP" },
                  ]}
                  onChange={(discountType) => setForm({ ...form, discountType })}
                />
              </label>
              <label>Valor<input type="number" min="1" max={form.discountType === "percentage" ? "100" : undefined} disabled={editing?.appliedAccounts > 0} value={form.discountValue} onChange={(event) => setForm({ ...form, discountValue: event.target.value })} required /></label>
              <label>Tope de descuento<input type="number" min="1" disabled={editing?.appliedAccounts > 0 || form.discountType !== "percentage"} value={form.maximumDiscount} onChange={(event) => setForm({ ...form, maximumDiscount: event.target.value })} placeholder="Sin tope" /></label>
              <label>Compra mínima<input type="number" min="0" disabled={editing?.appliedAccounts > 0} value={form.minimumSubtotal} onChange={(event) => setForm({ ...form, minimumSubtotal: event.target.value })} /></label>
              <PromotionDateTimePicker label="Inicio" disabled={editing?.appliedAccounts > 0} value={form.startsAt} onChange={(startsAt) => setForm({ ...form, startsAt })} />
              <PromotionDateTimePicker label="Término" value={form.endsAt} onChange={(endsAt) => setForm({ ...form, endsAt })} />
              <label>Máximo de cuentas<input type="number" min={editing?.appliedAccounts || 1} value={form.maximumAccounts} onChange={(event) => setForm({ ...form, maximumAccounts: event.target.value })} placeholder="Sin máximo" /></label>
            </div>
            <footer>
              <label className="tablee-promotions__toggle">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                <span className="tablee-promotions__toggle-track" aria-hidden="true">
                  <span />
                </span>
                <span className="tablee-promotions__toggle-label">
                  Código activo
                  <small>{form.isActive ? "Habilitado" : "Deshabilitado"}</small>
                </span>
              </label>
              <div className="tablee-promotions__footer-actions">
                <button type="button" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button type="submit" disabled={saving}>{saving ? "Guardando..." : "Guardar código"}</button>
              </div>
            </footer>
          </form>
        </div>
      ) : null}

      {usagePromotion ? (
        <div className="tablee-promotions__backdrop" role="presentation" onMouseDown={() => setUsagePromotion(null)}>
          <section className="tablee-promotions__modal" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><span>Aplicaciones</span><h2>{usagePromotion.code}</h2></div><button type="button" onClick={() => setUsagePromotion(null)}>×</button></header>
            <div className="tablee-promotions__usage-list">
              {redemptions.map((redemption) => (
                <article key={redemption.id}><div><strong>{redemption.email}</strong><small>{redemption.orderPublicId || redemption.status}</small></div><div><strong>-{formatMoney(redemption.discount)}</strong><small>{formatDate(redemption.redeemedAt || redemption.createdAt)}</small></div></article>
              ))}
              {!redemptions.length ? <p>Aún no hay aplicaciones para este código.</p> : null}
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

export default TableePromotions;
